import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { downloadPdf as downloadPdfLib } from "@/lib/pdf";
import {
  ShoppingCart, Search, Download, Trash2, FileText,
  Plus, X, Save, Check, Package, Truck, Printer, AlertCircle, RefreshCw
} from "lucide-react";
import { toast } from "sonner";
import { confirmAction } from "@/lib/confirm";
const STATUS = {
  to_order: { label: "À commander", short: "À commander", color: "#FFC400", bg: "rgba(255, 196, 0, 0.14)", icon: AlertCircle },
  in_stock: { label: "En stock",    short: "En stock",    color: "#9A9CA2", bg: "rgba(154, 156, 162, 0.14)", icon: Package },
  ordered:  { label: "Commandé ✓",  short: "Commandé",    color: "#FFC400", bg: "rgba(255, 196, 0, 0.10)", icon: Truck },
};

// Backward compat : déduire le status si absent
const getStatus = (it) => {
  if (it.status) return it.status;
  if (it.ordered) return "ordered";
  if (it.in_stock) return "in_stock";
  return "to_order";
};

export default function MaterialLists() {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [editor, setEditor] = useState(null);

  const load = async () => {
    const { data } = await api.get("/material-lists");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const filtered = items.filter((it) => {
    if (filterStatus) {
      const hasAny = (it.items || []).some((x) => getStatus(x) === filterStatus);
      if (!hasAny) return false;
    }
    if (!search) return true;
    const s = `${it.title || ""} ${it.quote_number || ""} ${it.client_name || ""} ${it.project_name || ""}`.toLowerCase();
    return s.includes(search.toLowerCase());
  });

  const downloadPdf = async (id, filter = null) => {
    const path = filter ? `/material-lists/${id}/pdf?filter=${filter}` : `/material-lists/${id}/pdf`;
    const suffix = filter === "ordered" ? "recap-commandes" : filter === "to_order" ? "a-commander" : "liste";
    await downloadPdfLib(path, `${suffix}.pdf`);
  };

  const remove = async (id) => {
    if (!(await confirmAction("Supprimer cette fiche ?"))) return;
    await api.delete(`/material-lists/${id}`);
    await load();
    toast.success("Fiche supprimée");
  };

  const regenerate = async (id) => {
    if (!(await confirmAction(
      "Re-agréger les matériaux depuis le devis ? Les statuts manuels (en stock, commandé) seront préservés.",
      { confirmLabel: "Régénérer" }
    ))) return;
    try {
      await api.post(`/material-lists/${id}/regenerate`);
      await load();
      toast.success("Fiche régénérée depuis le devis");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de la régénération");
    }
  };

  // Stats globales
  const totalItems = items.reduce((a, b) => a + (b.items?.length || 0), 0);
  const counts = items.reduce((acc, l) => {
    for (const it of (l.items || [])) {
      const s = getStatus(it);
      acc[s] = (acc[s] || 0) + 1;
    }
    return acc;
  }, {});

  return (
    <div data-testid="material-lists-page" className="max-w-7xl mx-auto">
      <header className="mb-10 animate-in">
        <div className="flex items-center gap-2 font-body text-xs font-semibold mb-3" style={{ color: "#FFC400" }}>
          <ShoppingCart size={12} strokeWidth={2} /> Matériel à commander
        </div>
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <h1 className="font-display text-[2.5rem] sm:text-5xl tracking-tight leading-[1.05]" style={{ fontWeight: 500 }}>
              Matériel à <span className="font-display" style={{ color: "#FFC400" }}>commander</span>
            </h1>
            <p className="text-secondary-text mt-3 max-w-3xl font-body text-[15px]">
              Pour chaque devis accepté, la liste des matériaux à acheter est générée automatiquement.
              Marquez chaque ligne <b>En stock</b>, <b>À commander</b> ou <b>Commandé</b> au fil de l'eau.
              Imprimez la liste finale des produits effectivement commandés en un clic.
            </p>
          </div>
        </div>
      </header>

      {/* Statut global compact */}
      {totalItems > 0 && (
        <div className="grid grid-cols-3 gap-3 mb-6 animate-in delay-1">
          {Object.entries(STATUS).map(([k, meta]) => {
            const Icon = meta.icon;
            const n = counts[k] || 0;
            return (
              <div key={k} className="card-minimal flex items-center gap-3" data-testid={`status-stat-${k}`}>
                <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: meta.bg, color: meta.color }}>
                  <Icon size={16} strokeWidth={2} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="numerals text-xl leading-none" style={{ color: meta.color }}>{n}</div>
                  <div className="font-body text-[11px] font-medium text-tertiary-text mt-1">{meta.label}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Toolbar */}
      <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-tertiary-text" />
            <input className="input pl-9 w-72" placeholder="Devis, client, projet..." value={search} onChange={(e) => setSearch(e.target.value)} data-testid="ml-search" />
          </div>
          <select className="input w-48" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} data-testid="ml-filter">
            <option value="">Tous statuts</option>
            <option value="to_order">À commander</option>
            <option value="in_stock">En stock</option>
            <option value="ordered">Commandé</option>
          </select>
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="card text-center py-16" data-testid="ml-empty">
          <ShoppingCart size={42} className="mx-auto mb-4 text-tertiary-text opacity-50" strokeWidth={1.5} />
          <div className="font-display text-xl mb-2" style={{ fontWeight: 600 }}>Aucune liste de commande</div>
          <div className="text-sm text-secondary-text font-body max-w-md mx-auto">
            Les listes sont créées automatiquement dès qu'un devis passe au statut <b>Accepté</b>.
            Allez dans <b>Devis</b> et cliquez sur le bouton vert <b>Accepté</b>.
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {filtered.map((list) => {
            const itemsArr = list.items || [];
            const byStatus = itemsArr.reduce((acc, it) => {
              const s = getStatus(it);
              acc[s] = (acc[s] || 0) + 1;
              return acc;
            }, {});
            const pendingHere = byStatus.to_order || 0;
            const orderedHere = byStatus.ordered || 0;
            return (
              <div key={list.id} className="card p-0 overflow-hidden animate-in" data-testid={`ml-${list.id}`}>
                <div className="px-6 py-4 flex items-center justify-between flex-wrap gap-3" style={{ background: "#F5F4F2", color: "#161618" }}>
                  <div className="flex items-center gap-3">
                    <FileText size={18} strokeWidth={2} style={{ color: "#FFC400" }} />
                    <div>
                      <div className="font-display text-lg tracking-tight" style={{ fontWeight: 600 }}>{list.quote_number || "—"}</div>
                      <div className="font-body text-[11px]  opacity-60">
                        {list.client_name || "Sans client"} <span className="opacity-50">·</span> {list.project_name || "Sans projet"}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {pendingHere > 0 && (
                      <span className="pulse-dot" style={{ minWidth: 28 }} data-testid={`pending-${list.id}`}>{pendingHere}</span>
                    )}
                    <span className="text-[11px] font-medium opacity-70">
                      {orderedHere} / {itemsArr.length} commandé{orderedHere > 1 ? "s" : ""}
                    </span>
                  </div>
                </div>

                <div className="p-5 flex items-center justify-between flex-wrap gap-2 border-b" style={{ borderColor: "#3A3D42" }}>
                  <div className="flex items-center gap-3 text-sm text-secondary-text font-body">
                    <span><b>{byStatus.to_order || 0}</b> à commander</span>
                    <span>·</span>
                    <span><b>{byStatus.in_stock || 0}</b> en stock</span>
                    <span>·</span>
                    <span><b>{byStatus.ordered || 0}</b> commandés</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button onClick={() => setEditor(list)} className="btn btn-sm btn-primary" data-testid={`ml-edit-${list.id}`}>
                      <Check size={12} /> Gérer les statuts
                    </button>
                    <button onClick={() => downloadPdf(list.id, "to_order")} className="btn btn-sm btn-outline" data-testid={`ml-pdf-pending-${list.id}`}>
                      <Printer size={12} /> Liste à commander
                    </button>
                    <button
                      onClick={() => downloadPdf(list.id, "ordered")}
                      disabled={orderedHere === 0}
                      className="btn btn-sm btn-accent"
                      data-testid={`ml-pdf-ordered-${list.id}`}
                      title={orderedHere === 0 ? "Marquez d'abord des lignes comme commandées" : "Imprimer le récapitulatif des commandes passées"}
                    >
                      <Download size={12} /> Récap commandés
                    </button>
                    <button onClick={() => regenerate(list.id)} className="btn btn-sm btn-ghost" title="Re-agréger depuis le devis (préserve les statuts manuels)" data-testid={`ml-regen-${list.id}`}>
                      <RefreshCw size={12} />
                    </button>
                    <button onClick={() => remove(list.id)} className="btn btn-sm btn-ghost" style={{ color: "#E10600" }} data-testid={`ml-del-${list.id}`}>
                      <Trash2 size={12} />
                    </button>
                  </div>
                </div>

                {/* Aperçu compact */}
                <div className="px-5 pb-4 pt-3">
                  {itemsArr.slice(0, 4).map((it, idx) => {
                    const meta = STATUS[getStatus(it)];
                    return (
                      <div key={idx} className="flex items-center justify-between gap-3 py-1.5 text-sm border-b last:border-b-0" style={{ borderColor: "#1F1F22" }}>
                        <span className="flex-1 truncate">{it.name}</span>
                        <span className="numerals text-xs text-tertiary-text">{it.quantity} {it.unit}</span>
                        <span className="badge" style={{ background: meta.bg, color: meta.color, fontSize: 10 }}>{meta.short}</span>
                      </div>
                    );
                  })}
                  {itemsArr.length > 4 && (
                    <div className="text-[11px] font-medium text-tertiary-text pt-2">
                      + {itemsArr.length - 4} autre{itemsArr.length - 4 > 1 ? "s" : ""} ligne{itemsArr.length - 4 > 1 ? "s" : ""}…
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {editor && (
        <ListEditor
          list={editor}
          onClose={() => setEditor(null)}
          onSaved={async () => { await load(); setEditor(null); }}
        />
      )}
    </div>
  );
}

function ListEditor({ list, onClose, onSaved }) {
  const [items, setItems] = useState((list.items || []).map((it) => ({ ...it, status: getStatus(it) })));
  const [notes, setNotes] = useState(list.notes || "");
  const [saving, setSaving] = useState(false);

  const updateItem = (idx, patch) => {
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  };
  const setItemStatus = (idx, status) => {
    updateItem(idx, {
      status,
      // sync bools for backward compat
      ordered: status === "ordered",
      in_stock: status === "in_stock",
    });
  };
  const removeItem = (idx) => setItems((prev) => prev.filter((_, i) => i !== idx));
  const addItem = () => setItems((prev) => [...prev, { name: "", quantity: 0, unit: "u", note: "", status: "to_order" }]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/material-lists/${list.id}`, {
        kind: list.kind,
        quote_id: list.quote_id,
        client_id: list.client_id,
        project_name: list.project_name,
        items,
        notes,
      });
      toast.success("Liste enregistrée");
      window.dispatchEvent(new CustomEvent("ml:updated"));
      await onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-surface rounded-xl shadow-2xl max-w-4xl w-full max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="ml-editor">
        <div className="px-6 py-4 border-b flex items-center justify-between" style={{ borderColor: "#3A3D42", background: "rgba(255,196,0,0.10)", color: "#7A4F1F" }}>
          <div className="flex items-center gap-3">
            <ShoppingCart size={18} strokeWidth={2} />
            <div>
              <div className="font-body text-[11px] font-medium opacity-70">Bon de commande</div>
              <div className="font-display text-lg tracking-tight" style={{ fontWeight: 600 }}>{list.title || `Matériel à commander — ${list.quote_number || ""}`}</div>
            </div>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-lg hover:bg-black/10 flex items-center justify-center"><X size={16} /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          <div className="rounded-lg px-4 py-3 text-xs font-body border" style={{ background: "#1F1F22", borderColor: "#3A3D42" }}>
            Cliquez sur l'un des 3 statuts pour chaque ligne :
            <span className="badge ml-2" style={{ background: STATUS.to_order.bg, color: STATUS.to_order.color }}>{STATUS.to_order.label}</span>
            <span className="badge ml-2" style={{ background: STATUS.in_stock.bg, color: STATUS.in_stock.color }}>{STATUS.in_stock.label}</span>
            <span className="badge ml-2" style={{ background: STATUS.ordered.bg, color: STATUS.ordered.color }}>{STATUS.ordered.label}</span>
          </div>

          <div className="space-y-2">
            {items.length === 0 && (
              <div className="text-center text-tertiary-text py-8 font-body text-sm">
                Aucun matériau. Cliquez sur <b>Ajouter</b>.
              </div>
            )}
            {items.map((it, idx) => {
              const meta = STATUS[it.status] || STATUS.to_order;
              return (
                <div key={idx} className="p-3 rounded-lg border" data-testid={`ml-row-${idx}`} style={{
                  borderColor: "#3A3D42",
                  background: it.status === "ordered" ? "#FDF7EE" : it.status === "in_stock" ? "rgba(255,196,0,0.10)" : "transparent",
                }}>
                  <div className="grid grid-cols-12 gap-2 mb-2">
                    <input className="input col-span-5" placeholder="Nom du matériau" value={it.name} onChange={(e) => updateItem(idx, { name: e.target.value })} data-testid={`ml-name-${idx}`} />
                    <input type="number" step="0.01" className="input col-span-2 text-right numerals" placeholder="Qté" value={it.quantity} onChange={(e) => updateItem(idx, { quantity: Number(e.target.value) || 0 })} />
                    <input className="input col-span-2" placeholder="Unité" value={it.unit} onChange={(e) => updateItem(idx, { unit: e.target.value })} />
                    <input className="input col-span-2" placeholder="Fournisseur..." value={it.note || ""} onChange={(e) => updateItem(idx, { note: e.target.value })} />
                    <button onClick={() => removeItem(idx)} className="col-span-1 w-9 h-9 rounded-md border flex items-center justify-center" style={{ borderColor: "#3A3D42", color: "#E10600" }}>
                      <Trash2 size={13} />
                    </button>
                  </div>
                  {/* 3-state pills — état actif très visible (coche ✓ + fond plein + ring) */}
                  <div className="flex gap-2 flex-wrap" data-testid={`ml-status-pills-${idx}`}>
                    {Object.entries(STATUS).map(([k, m]) => {
                      const active = it.status === k;
                      const Icon = m.icon;
                      // Couleurs cibles : actif = surligné net (jaune/vert pour validé, gris pour "à commander")
                      const activeBg = k === "to_order" ? "#E10600" : "#FFC400";
                      const activeFg = k === "to_order" ? "#FFFFFF" : "#0B0B0C";
                      return (
                        <button
                          key={k}
                          onClick={() => setItemStatus(idx, k)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all"
                          style={{
                            background: active ? activeBg : "transparent",
                            color: active ? activeFg : "var(--ink-soft)",
                            border: active ? `2px solid ${activeBg}` : "1.5px solid var(--steel)",
                            boxShadow: active ? `0 0 0 3px ${activeBg}33` : "none",
                          }}
                          data-testid={`ml-status-${idx}-${k}`}
                          aria-pressed={active}
                        >
                          {active ? <Check size={13} strokeWidth={3} /> : <Icon size={12} strokeWidth={2.5} />}
                          <span>{m.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
          <button onClick={addItem} className="btn btn-outline btn-sm" data-testid="ml-add">
            <Plus size={13} /> Ajouter une ligne
          </button>

          <div>
            <label className="label">Notes pour l'équipe</label>
            <textarea className="input" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Ex: code accès chantier, fournisseur préféré..." />
          </div>
        </div>

        <div className="px-6 py-4 border-t bg-soft flex justify-between gap-2 rounded-b-xl" style={{ borderColor: "#3A3D42" }}>
          <button onClick={onClose} className="btn btn-outline btn-sm">Annuler</button>
          <button onClick={save} disabled={saving} className="btn btn-primary" data-testid="ml-save">
            <Save size={14} /> {saving ? "..." : "Enregistrer"}
          </button>
        </div>
      </div>
    </div>
  );
}
